/**
 * 「TA 的手机」首页用的几样说法（纯函数，PhoneHome 用）：每个聊天的最后一句、时间、未读角标、屏幕上方的钟。
 * 只说消息里真有的：没有消息就不编一句。
 */
import type { MessageSummary } from "@/shared/contracts";
import { photoStage } from "../photoStage";

export type PhoneChat = "private" | "family";

const WEEKDAY = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
const pad = (n: number) => String(n).padStart(2, "0");

/** 消息在对话里出现的时刻（同 module.tsx 的 shownAt：排队的回复按 available_at，没有就用写入时刻） */
export const messageAt = (m: MessageSummary): number => Date.parse(m.available_at ?? m.created_at) || 0;

/** 这个聊天里最新的一条。一个人住的家没有家庭群：两条频道都算“我和 TA” */
export function lastMessage(items: readonly MessageSummary[] | null | undefined, chat: PhoneChat, solo: boolean): MessageSummary | null {
  let last: MessageSummary | null = null;
  for (const m of items ?? []) {
    const channel = m.channel ?? "private";
    if (!solo && channel !== chat) continue;
    if (solo && chat === "family") continue;
    if (!last || messageAt(m) >= messageAt(last)) last = m;
  }
  return last;
}

/** 真实正文优先；没有正文才按当前附件状态预览，未就绪 URL 不证明照片已到。 */
export function previewText(m: MessageSummary | null, chat: PhoneChat, petName: string): string | null {
  if (!m) return null;
  // checking（I108，问照片那一条）和 processing 同一档「还在冲洗」（photoStage）
  const stage = photoStage(m.photo_status);
  const attachment = stage === "processing" ? "照片还在冲洗"
    : stage === "unknown" ? "这张照片还没确认"
    : stage === "failed" ? "照片没冲洗出来"
    : stage === "ready" ? m.photo_url ? "发来一张照片" : "附图暂不可显示"
    : stage == null && m.photo_url ? "寄来一张纸质明信片" : "";
  const text = m.text.replace(/\s+/g, " ").trim() || attachment;
  if (!text) return null;
  if (m.sender === "owner") return `我：${text}`;
  return chat === "family" ? `${petName}：${text}` : text;
}

/** 列表里的时间：今天写几点几分，昨天写“昨天”，再早写“10 月 1 日”，不是今年的带年份（看的人所在时区） */
export function chatTime(ms: number, now: number): string | null {
  if (!ms) return null;
  const at = new Date(ms);
  const today = new Date(now);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  if (ms >= startOfToday) return `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  if (ms >= startOfToday - 86_400_000) return "昨天";
  const md = `${at.getMonth() + 1} 月 ${at.getDate()} 日`;
  return at.getFullYear() === today.getFullYear() ? md : `${at.getFullYear()} 年 ${md}`;
}

/** 群里每条消息下面的时间：今天写几点几分，昨天写“昨天 几点几分”，再早写“几月几日 几点几分”，不是今年带年份（看的人所在时区） */
export function messageTime(ms: number, now: number): string | null {
  const day = chatTime(ms, now);
  if (!day) return null;
  const at = new Date(ms);
  const hm = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  return day === hm ? hm : `${day} ${hm}`;
}

/** 未读角标：0 不挂，过 99 写 99+ */
export function unreadBadge(count: number): string | null {
  if (!(count > 0)) return null;
  return count > 99 ? "99+" : String(count);
}

/** 屏幕上方的钟：几点几分、几月几日星期几（看的人所在时区） */
export function phoneClock(now: number): { time: string; date: string } {
  const d = new Date(now);
  return { time: `${pad(d.getHours())}:${pad(d.getMinutes())}`, date: `${d.getMonth() + 1} 月 ${d.getDate()} 日 ${WEEKDAY[d.getDay()]}` };
}
